import {
    Button,
    Col,
    Form,
    FormGroup,
    Input,
    InputGroup,
    InputGroupText,
    Row,
    Spinner,
} from "reactstrap";
import React from "react";
import { FaEye, FaEyeSlash } from "react-icons/fa"; // Import eye icons from react-icons
import withNavigateHook from "../../hooks/withNavigationHook";
import { createWorshipBibleTeamUser } from "../../components/helpers/RegistrationHelpers";

class RegisterForm extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            formSubmitted: false,
            teamName: "",
            firstName: "",
            lastName: "",
            email: "",
            password: "",
            password1: "",
            privacyPolicy: false,
            teamNameError: "",
            firstNameError: "",
            lastNameError: "",
            emailError: "",
            passwordError: "",
            password1Error: "",
            privacyPolicyError: "",
            generalError: "",
            lastSubmitted: 0, // For rate limiting: store the timestamp of the last submission
            showPassword: false,         // Toggle flag for password visibility
            showPasswordConfirm: false,  // Toggle flag for confirm password visibility
            couponCode: "",
            couponCodeError: "",
        };

        sessionStorage.removeItem("clientSecret");
    }

    componentDidMount() {}

    onChangeTeamName = (e) => {
        this.setState({ teamName: e.target.value, teamNameError: "" });
    };

    onChangeFirstName = (e) => {
        this.setState({ firstName: e.target.value, firstNameError: "" });
    };

    onChangeLastName = (e) => {
        this.setState({ lastName: e.target.value, lastNameError: "" });
    };

    onChangeEmail = (e) => {
        this.setState({ email: e.target.value, emailError: "" });
    };

    // Update the onChangePassword handler to immediately check matching
    onChangePassword = (e) => {
        this.setState({ password: e.target.value, passwordError: "" }, () => {
            // If the confirm password field has a value, compare immediately.
            if (this.state.password1) {
                if (this.state.password !== this.state.password1) {
                    this.setState({ password1Error: "Passwords do not match" });
                } else {
                    this.setState({ password1Error: "" });
                }
            }
        });
    };

    // Update the onChangePassword1 handler to immediately check matching
    onChangePassword1 = (e) => {
        this.setState({ password1: e.target.value, password1Error: "" }, () => {
            if (this.state.password && this.state.password !== this.state.password1) {
                this.setState({ password1Error: "Passwords do not match" });
            } else {
                this.setState({ password1Error: "" });
            }
        });
    };

    onChangeCouponCode = (e) => {
        this.setState({ couponCode: e.target.value, couponCodeError: "" });
    };

    onChangePrivacyPolicy = (e) => {
        // Use the checked property to allow toggle
        this.setState({ privacyPolicy: e.target.checked, privacyPolicyError: "" });
    };

    togglePasswordVisibility = () => {
        this.setState((prevState) => ({
            showPassword: !prevState.showPassword,
        }));
    };

    togglePasswordConfirmVisibility = () => {
        this.setState((prevState) => ({
            showPasswordConfirm: !prevState.showPasswordConfirm,
        }));
    };

    validateAndSubmitForm = () => {
        const now = Date.now();

        // Mark form as submitting
        this.setState({ formSubmitted: true, generalError: "" });

        let errors = {};
        let formHasError = false;

        if (!this.state.teamName || this.state.teamName.trim().length === 0) {
            errors.teamNameError = "* You must provide a church or group name";
            formHasError = true;
        }
        if (!this.state.firstName || this.state.firstName.trim().length === 0) {
            errors.firstNameError = "* You must provide a first name";
            formHasError = true;
        }
        if (!this.state.lastName || this.state.lastName.trim().length === 0) {
            errors.lastNameError = "* You must provide a last name";
            formHasError = true;
        }
        if (!this.state.email || this.state.email.trim().length === 0) {
            errors.emailError = "* You must provide an email address";
            formHasError = true;
        } else {
            // Validate email address using a regex
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(this.state.email)) {
                errors.emailError = "* Please enter a valid email address";
                formHasError = true;
            }
        }
        if (!this.state.password || this.state.password.trim().length === 0) {
            errors.passwordError = "* You must provide a password";
            formHasError = true;
        } else if (this.state.password.trim().length < 8) {
            errors.passwordError = "* Password must be at least 8 characters long";
            formHasError = true;
        }
        if (!this.state.password1 || this.state.password1.trim().length === 0) {
            errors.password1Error = "* You must confirm the provided password";
            formHasError = true;
        }
        if (!this.state.privacyPolicy) {
            errors.privacyPolicyError =
                "* You must confirm you agree to the privacy policy";
            formHasError = true;
        }
        // Final check for password matching
        if (
            this.state.password &&
            this.state.password1 &&
            this.state.password !== this.state.password1
        ) {
            errors.password1Error = "Confirmed password does not match password";
            formHasError = true;
        }

        if (formHasError) {
            // Set all errors at once and mark form as not submitting
            this.setState({ ...errors, formSubmitted: false });
            return;
        } else {
            // Update timestamp for valid submissions
            this.setState({ lastSubmitted: now });
            // Call registration helper (assumes it returns a Promise)
            createWorshipBibleTeamUser(
                this.props,
                this.state.firstName,
                this.state.lastName,
                this.state.email,
                this.state.password,
                this.state.teamName,
                this.state.couponCode
            )
                .then((paymentResponse) => {
                    if (!paymentResponse.clientSecret) {
                        // ✅ No payment needed — redirect to success page
                        this.props.navigation('/register/successful');
                    } else {
                        // ✅ Payment required — go to billing flow
                        sessionStorage.setItem("clientSecret", paymentResponse.clientSecret);
                        sessionStorage.setItem("intentType", paymentResponse.intentType);
                        sessionStorage.setItem("couponCode", this.state.couponCode);
                        this.props.navigation('/register/billing');
                    }
                })
                .catch((error) => {
                    this.setState({
                        formSubmitted: false,
                        generalError: error?.message || "An error occurred during registration.",
                    });
                });
        }
    };

    getPageError = () => {
        const error = sessionStorage.getItem("page-error");
        sessionStorage.removeItem("page-error");
        return error;
    };

    registerButtonAvailable = () => {
        return (
            this.state.teamName &&
            this.state.firstName &&
            this.state.lastName &&
            this.state.email &&
            this.state.password &&
            this.state.password1 &&
            this.state.privacyPolicy &&
            this.state.password === this.state.password1 &&
            !this.state.formSubmitted
        );
    };

    render() {
        return (
            <>
                <Form role="form">
                    {/* Church or group name */}
                    <FormGroup>
                        <Row lg="12">
                            <Col lg="12">
                                <InputGroup className="input-group-alternative mb-3">
                                    <InputGroupText>
                                        <i className="ni ni-building" />
                                    </InputGroupText>
                                    <Input
                                        name="teamName"
                                        placeholder="Church or group name"
                                        type="text"
                                        maxLength="100"
                                        onChange={this.onChangeTeamName}
                                        value={this.state.teamName}
                                    />
                                </InputGroup>
                            </Col>
                        </Row>
                        <Row lg="12">
                            <Col lg="12">
                                <span className="text-danger">{this.state.teamNameError}</span>
                            </Col>
                        </Row>
                    </FormGroup>
                    {/* First Name */}
                    <FormGroup>
                        <Row lg="12">
                            <Col lg="12">
                                <InputGroup className="input-group-alternative mb-3">
                                    <InputGroupText>
                                        <i className="ni ni-hat-3" />
                                    </InputGroupText>
                                    <Input
                                        name="firstName"
                                        placeholder="First Name"
                                        type="text"
                                        maxLength="50"
                                        onChange={this.onChangeFirstName}
                                        value={this.state.firstName}
                                    />
                                </InputGroup>
                            </Col>
                        </Row>
                        <Row lg="12">
                            <Col lg="12">
                                <span className="text-danger">
                                  {this.state.firstNameError}
                                </span>
                            </Col>
                        </Row>
                    </FormGroup>
                    {/* Last Name */}
                    <FormGroup>
                        <Row lg="12">
                            <Col lg="12">
                                <InputGroup className="input-group-alternative mb-3">
                                    <InputGroupText>
                                        <i className="ni ni-hat-3" />
                                    </InputGroupText>
                                    <Input
                                        name="lastName"
                                        placeholder="Last Name"
                                        type="text"
                                        maxLength="50"
                                        onChange={this.onChangeLastName}
                                        value={this.state.lastName}
                                    />
                                </InputGroup>
                            </Col>
                        </Row>
                        <Row lg="12">
                            <Col lg="12">
                                <span className="text-danger">
                                  {this.state.lastNameError}
                                </span>
                            </Col>
                        </Row>
                    </FormGroup>
                    {/* Email */}
                    <FormGroup>
                        <Row lg="12">
                            <Col lg="12">
                                <InputGroup className="input-group-alternative mb-3">
                                    <InputGroupText>
                                        <i className="ni ni-email-83" />
                                    </InputGroupText>
                                    <Input
                                        name="email"
                                        placeholder="Email"
                                        type="email"
                                        maxLength="200"
                                        onChange={this.onChangeEmail}
                                        value={this.state.email}
                                    />
                                </InputGroup>
                            </Col>
                        </Row>
                        <Row lg="12">
                            <Col lg="12">
                                <span className="text-danger">{this.state.emailError}</span>
                            </Col>
                        </Row>
                    </FormGroup>
                    {/* Password */}
                    <FormGroup>
                        <Row lg="12">
                            <Col lg="12">
                                <InputGroup className="input-group-alternative mb-3">
                                    <InputGroupText>
                                        <i className="ni ni-lock-circle-open" />
                                    </InputGroupText>
                                    <Input
                                        name="password"
                                        placeholder="Password"
                                        type={this.state.showPassword ? "text" : "password"}
                                        autoComplete="off"
                                        onChange={this.onChangePassword}
                                        value={this.state.password}
                                    />
                                    {/* Toggle button for password visibility */}
                                    <InputGroupText
                                        onClick={this.togglePasswordVisibility}
                                        style={{ cursor: "pointer" }}
                                    >
                                        {this.state.showPassword ? <FaEyeSlash /> : <FaEye />}
                                    </InputGroupText>
                                </InputGroup>
                            </Col>
                        </Row>
                        <Row lg="12">
                            <Col lg="12">
                                <span className="text-danger">
                                  {this.state.passwordError}
                                </span>
                            </Col>
                        </Row>
                    </FormGroup>
                    {/* Confirm Password */}
                    <FormGroup>
                        <Row lg="12">
                            <Col lg="12">
                                <InputGroup className="input-group-alternative mb-3">
                                    <InputGroupText>
                                        <i className="ni ni-lock-circle-open" />
                                    </InputGroupText>
                                    <Input
                                        name="passwordConfirm"
                                        placeholder="Confirm Password"
                                        type={this.state.showPasswordConfirm ? "text" : "password"}
                                        autoComplete="off"
                                        onChange={this.onChangePassword1}
                                        value={this.state.password1}
                                    />
                                    {/* Toggle button for confirm password visibility */}
                                    <InputGroupText
                                        onClick={this.togglePasswordConfirmVisibility}
                                        style={{ cursor: "pointer" }}
                                    >
                                        {this.state.showPasswordConfirm ? <FaEyeSlash /> : <FaEye />}
                                    </InputGroupText>
                                </InputGroup>
                            </Col>
                        </Row>
                        <Row lg="12">
                            <Col lg="12">
                                <span className="text-danger">
                                  {this.state.password1Error}
                                </span>
                            </Col>
                        </Row>
                    </FormGroup>
                    {/* Coupon Code */}
                    <FormGroup>
                        <Row lg="12">
                            <Col lg="12">
                                <InputGroup className="input-group-alternative mb-3">
                                    <InputGroupText>
                                        <i className="ni ni-tag" />
                                    </InputGroupText>
                                    <Input
                                        name="couponCode"
                                        placeholder="Promotion Code (optional)"
                                        type="text"
                                        maxLength="50"
                                        onChange={this.onChangeCouponCode}
                                        value={this.state.couponCode}
                                    />
                                </InputGroup>
                            </Col>
                        </Row>
                        <Row lg="12">
                            <Col lg="12">
                                <span className="text-danger">{this.state.couponCodeError}</span>
                            </Col>
                        </Row>
                    </FormGroup>
                    {/* Privacy Policy Checkbox */}
                    <Row className="my-4">
                        <Col xs="12">
                            <div className="custom-control custom-control-alternative custom-checkbox">
                                <input
                                    name="privacyConfirmation"
                                    className="custom-control-input"
                                    id="customCheckRegister"
                                    type="checkbox"
                                    onChange={this.onChangePrivacyPolicy}
                                    checked={this.state.privacyPolicy}
                                />
                                <label
                                    className="custom-control-label"
                                    htmlFor="customCheckRegister"
                                >
                                  <span>
                                    I agree with the{" "}
                                      <a href="/privacy-policy">Privacy Policy</a> and <a href="/terms-and-conditions">Terms & Conditions</a>
                                  </span>
                                </label>
                            </div>
                        </Col>
                    </Row>
                    <Row lg="12">
                        <Col lg="12">
              <span className="text-danger">
                {this.state.privacyPolicyError}
              </span>
                        </Col>
                    </Row>
                    {/* General Error / Page Error */}
                    <Row lg="12">
                        <Col lg="12">
              <span className="text-danger">
                {this.getPageError() || this.state.generalError}
              </span>
                        </Col>
                    </Row>
                    {/* Registration Button */}
                    <div className="text-center">
                        <Button
                            id="registerButton"
                            className="mt-4"
                            color="primary"
                            type="button"
                            onClick={this.validateAndSubmitForm}
                            disabled={!this.registerButtonAvailable()}
                        >
                            {this.state.formSubmitted ? (
                                <>
                                    <div className="spinner-container">
                                        <Spinner animation="border" role="status">
                                            <span className="visually-hidden"></span>
                                        </Spinner>
                                        &nbsp;Registering...
                                    </div>
                                </>
                            ) : (
                                "Register"
                            )}
                        </Button>
                    </div>
                </Form>
            </>
        );
    }
}

export default withNavigateHook(RegisterForm);
